import {
  ClipboardDocumentIcon,
  EllipsisVerticalIcon
} from '@heroicons/react/20/solid'
import { useSuspenseQuery } from '@tanstack/react-query'
import type { KeycardTemplate } from 'cleverev-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useParams } from 'react-router'
import { toast } from 'sonner'
import { keycardAPI } from '#/api/keycard'
import { Button } from '#/components/ui/button'
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm
} from '#/components/ui/description-list'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '#/components/ui/dropdown-menu'
import { Input } from '#/components/ui/input'
import { Separator } from '#/components/ui/separator'
import { Heading, Subheading } from '#/components/ui/typography'
import { copy } from '#/lib/copy'
import { formatDateTime2Digit } from '#/lib/datetime'
import { DeleteKeycardModal } from '../@modal/delete/page'
import { TrashIcon } from 'lucide-react'


export default function KeycardPage() {
  const { t, i18n } = useTranslation([ 'keycards', 'general' ])
  const params = useParams() as { organization: string; keycard: string }
  const [ isEditing, setIsEditing ] = useState(false)
  const [ localKeycard, setLocalKeycard ] = useState<Partial<KeycardTemplate>>({})
  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState<{ id: string } | null>(null)

  const { data: keycard } = useSuspenseQuery({
    queryKey: [ 'organizations', params.organization, 'keycards', params.keycard ],
    queryFn: () => keycardAPI.get(params.keycard)
  })

  // Copy keycard id and notify
  function copyKeycardId(keycard: KeycardTemplate) {
    copy(keycard.id)
    toast.info(t('view_keycard.id_copied'))
  }

  function startEditing() {
    setIsEditing(true)
    setLocalKeycard(keycard)
  }

  function cancelEditing() {
    setIsEditing(false)
    setLocalKeycard({})
  }

  function renderField(key: keyof KeycardTemplate, fallback = '-') {
    const value = isEditing
      ? localKeycard[key] ?? ''
      : keycard[key] ?? fallback

    if (!isEditing) return <>{value}</>

    return (
      <Input
        className="w-full border px-2 rounded"
        type="text"
        value={value}
        onChange={e =>
          setLocalKeycard({ ...localKeycard, [key]: e.target.value })
        }
      />
    )
  }

  async function handleSave() {
    try {
      await keycardAPI.update(params.keycard, localKeycard)
      toast.success(t('general:saved_successfully'))
      setIsEditing(false)
    } catch {
      toast.error(t('general:save_failed'))
    }
  }

  return (
    <div className="flex grow flex-col gap-4 items-start">

      <Outlet />

      {isDeleteModalOpen !== null
      && (<DeleteKeycardModal
        id={isDeleteModalOpen.id}
        onClose={() => setIsDeleteModalOpen(null)}
      />)}

      {keycard && (
        <>
          <div className="flex w-full items-end justify-between gap-4">
            <Heading>{`${t('view_keycard.keycard')} ${keycard.id}`}</Heading>
            <div className="flex flex-row gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <Button variant="ghost" size="icon">
                    <EllipsisVerticalIcon className="text-tertiary" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => copyKeycardId(keycard)}>
                    <ClipboardDocumentIcon className="text-tertiary" />
                    {t('view_keycard.menu.copy_id')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={isEditing ? handleSave : startEditing}>
                    <ClipboardDocumentIcon className="text-tertiary" />
                    {isEditing ? t('general:save') : t('general:edit')}
                  </DropdownMenuItem>
                  {isEditing && (
                    <DropdownMenuItem onClick={cancelEditing}>
                      {t('general:cancel')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() =>
                    setIsDeleteModalOpen({
                      id: keycard.id
                    })}>
                    <TrashIcon className="text-tertiary" />
                    {t('keycard_list.menu.remove')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <Separator />

          <div className="flex w-full items-center justify-between">
            <Subheading>{t('view_keycard.details')}</Subheading>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={isEditing ? handleSave : startEditing}
              >
                {isEditing ? t('general:save') : t('general:edit')}
              </Button>
              {isEditing && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={cancelEditing}
                >
                  {t('general:cancel')}
                </Button>
              )}
            </div>
          </div>

          <DescriptionList className="w-full">
            <DescriptionTerm>
              {t('fields.id')}
            </DescriptionTerm>
            <DescriptionDetails>
              {keycard.id}
            </DescriptionDetails>
            <DescriptionTerm>
              {t('fields.friendlyId')}
            </DescriptionTerm>
            <DescriptionDetails>
              {renderField('friendlyId')}
            </DescriptionDetails>
            <DescriptionTerm>
              {t('fields.created_at')}
            </DescriptionTerm>
            <DescriptionDetails>
              {formatDateTime2Digit(keycard.createdAt, i18n.language)}
            </DescriptionDetails>
            <DescriptionTerm>
              {t('fields.updated_at')}
            </DescriptionTerm>
            <DescriptionDetails>
              {formatDateTime2Digit(keycard.updatedAt, i18n.language)}
            </DescriptionDetails>
          </DescriptionList>
        </>
      )}

    </div>
  )
}
